import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/AccountingUser';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const ACCOUNTING_USER_API_REDUCER_KEY = 'accounting-users';
export const ACCOUNTING_USER_PATH = 'accounting-users';

export const accountingUserApi = createApi({
    reducerPath: ACCOUNTING_USER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['AccountingUser', 'AccountingUsers'],
    endpoints: (builder) => ({
        getAccountingUserList: builder.query<PaginationResult<Entity.AccountingUser>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['AccountingUsers'],
        }),
        getAccountingUser: builder.query<Entity.AccountingUser, Entity.PrimaryAccountingUser>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = accountingUserNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ ACCOUNTING_USER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['AccountingUser'],
        }),
        getAccountingUserMultiple: builder.query<Entity.AccountingUser, Entity.PrimaryAccountingUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }/ids${Entity.PrimaryAccountingUser.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['AccountingUsers'],
        }),
        getAccountingUserPresentation: builder.query<PaginationResult<Entity.AccountingUser>, Entity.PresentationAccountingUserRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ ACCOUNTING_USER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['AccountingUsers'],
        }),
        
        insertAccountingUser: builder.mutation<Entity.AccountingUser, Entity.CreateAccountingUser>({
            query: (toInsert) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    accountingUserApi.util.upsertQueryData('getAccountingUser', Entity.PrimaryAccountingUser.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['AccountingUsers'],
        }),
        insertMultipleAccountingUser: builder.mutation<Entity.AccountingUser[], Entity.CreateAccountingUser[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            accountingUserApi.util.upsertQueryData('getAccountingUser', Entity.PrimaryAccountingUser.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['AccountingUsers'],
        }),
        updateAccountingUser: builder.mutation<Entity.AccountingUser, Entity.UpdateEntityAccountingUser>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['AccountingUsers'],
        }),
        updateMultipleAccountingUser: builder.mutation<Entity.AccountingUser[], Entity.UpdateEntityAccountingUser[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['AccountingUsers'],
        }),
        deleteAccountingUser: builder.mutation<Entity.AccountingUser, Entity.AccountingUser>({
            query: (toDelete) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }/${Entity.PrimaryAccountingUser.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['AccountingUsers'],
        }),
        deleteMultipleAccountingUser: builder.mutation<Entity.AccountingUser[], Entity.PrimaryAccountingUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ ACCOUNTING_USER_PATH }/batch${Entity.PrimaryAccountingUser.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['AccountingUsers'],
        }),
    }),
})

export const [accountingUserNormalizer, accountingUserNormalizerMiddleware] = createNormalizationMiddleware(ACCOUNTING_USER_API_REDUCER_KEY, accountingUserApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetAccountingUserListQuery,
    useGetAccountingUserMultipleQuery,
    useGetAccountingUserPresentationQuery,
    useGetAccountingUserQuery,
    useInsertAccountingUserMutation,
    useInsertMultipleAccountingUserMutation,
    useUpdateAccountingUserMutation,
    useUpdateMultipleAccountingUserMutation,
    useDeleteAccountingUserMutation,
    useDeleteMultipleAccountingUserMutation,
    
} = accountingUserApi
